import React, { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { type ColumnDef } from '@tanstack/react-table'
import DataTable from '../../components/DataTable'
import Button from '../components/Button'
import Icon from '../components/Icon'
import { useUsersStore } from '../../stores/usersStore'
import { useSchoolStore } from '../../stores/schoolsStore'
import { useAuth } from '../../contexts/AuthContext'
import { User, Permission, School } from '../../types'

const Users: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { users, loading, error, fetchUsers, removeUser } = useUsersStore()

  const { schools, loading: schoolsLoading, fetchSchools } = useSchoolStore()

  // Fetch users, organizations, and schools on component mount
  useEffect(() => {
    fetchUsers()
    fetchSchools()
  }, [fetchUsers, fetchSchools])

  const handleDeleteUser = async (
    id: string,
    firstName: string,
    lastName: string,
  ) => {
    if (
      window.confirm(
        `Are you sure you want to delete "${firstName} ${lastName}"? This action cannot be undone.`,
      )
    ) {
      try {
        await removeUser(id)
      } catch (err) {
        console.error('Error deleting user:', err)
      }
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatSchools = (
    userSchools: string[] | Array<{ _id: string; name: string }>,
  ) => {
    if (userSchools.length === 0) return 'No schools'

    // Check if schools are populated (objects) or just IDs (strings)
    if (typeof userSchools[0] === 'object') {
      // Schools are populated, extract names directly
      return (userSchools as Array<{ _id: string; name: string }>)
        .map((school) => school.name)
        .join(', ')
    } else {
      // Schools are just IDs, need to resolve from schools store
      const schoolNames = (userSchools as string[]).map((schoolId) => {
        const school = schools.find((s: School) => s._id === schoolId)
        return school ? school.displayName : 'Unknown School'
      })

      return schoolNames.join(', ')
    }
  }

  // Check if user has permission to create users
  const canCreateUsers = user?.permissions.includes(Permission.MANAGE_USERS)

  // Define columns for DataTable
  const columns = useMemo<ColumnDef<User>[]>(
    () => [
      {
        accessorKey: 'firstName',
        header: 'Name',
        cell: (info) => {
          const user = info.row.original
          return (
            <div className="text-sm font-medium text-gray-900">
              {user.firstName} {user.lastName}
            </div>
          )
        },
      },
      {
        accessorKey: 'email',
        header: 'Email',
        cell: (info) => (
          <div className="text-sm text-gray-900">
            {info.getValue() as string}
          </div>
        ),
      },
      {
        accessorKey: 'schools',
        header: 'Schools',
        cell: (info) => {
          const schools = info.getValue() as any[]
          return (
            <div
              className="text-sm text-gray-900"
              title={formatSchools(schools)}
            >
              {schools.length} school{schools.length !== 1 ? 's' : ''}
            </div>
          )
        },
      },
      {
        accessorKey: 'role',
        header: 'Role',
        cell: (info) => {
          const role = info.getValue()
          const roleName = typeof role === 'object' && role !== null
            ? (role as { name: string }).name
            : '—'
          return <div className="text-sm text-gray-500">{roleName}</div>
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: (info) => (
          <div className="text-sm text-gray-500">
            {formatDate(info.getValue() as string)}
          </div>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className="text-right text-sm font-medium flex">
            <Button
              onClick={(e) => {
                e.stopPropagation()
                navigate(`/manage/users/${row.original._id}`)
              }}
              variant="link"
              className="mr-3"
            >
              Edit
            </Button>
            {canCreateUsers && (
              <Button
                onClick={(e) => {
                  e.stopPropagation()
                  handleDeleteUser(
                    row.original._id,
                    row.original.firstName,
                    row.original.lastName,
                  )
                }}
                variant="link"
                className="text-red-600 hover:text-red-900"
              >
                Delete
              </Button>
            )}
          </div>
        ),
      },
    ],
    [schools],
  )

  if (loading || schoolsLoading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <p className="text-gray-600">Manage users in your system</p>
        </div>
        {canCreateUsers && (
          <Button
            onClick={() => navigate('/manage/users/new')}
            variant="primary"
            className="ml-auto flex items-center gap-2"
          >
            <Icon type="plus" className="w-5 h-5" />
            Create User
          </Button>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Users Table */}
      <DataTable
        data={users}
        columns={columns}
        searchPlaceholder="Search users..."
        emptyStateTitle="No users"
        emptyStateDescription="Get started by creating your first user."
        emptyStateIcon={
          <Icon type="users" className="mx-auto h-12 w-12 text-gray-400" />
        }
        onCreateClick={
          canCreateUsers ? () => navigate('/manage/users/new') : undefined
        }
        createButtonText="Create User"
        showCreateButton={canCreateUsers}
        loading={loading || schoolsLoading}
        onRowClick={(userItem) => navigate(`/manage/users/${userItem._id}`)}
      />
    </div>
  )
}

export default Users
